import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  Check,
  CheckCircle,
  ClipboardCheck,
  Info,
  KeyRound,
  Loader2,
  ShieldCheck,
  Sparkles,
  UserRoundCog,
  X,
} from 'lucide-react';
import { orgAPI } from '../services/api';
import { useAuth } from '../utils/AuthContext';

const CATEGORY_OPTIONS = [
  'employee_pii',
  'identity_documents',
  'contact_details',
  'financial_records',
  'family_member_data',
  'hr_records',
  'consent_evidence',
  'compliance_records',
];

const LOGIN_METHOD_OPTIONS = [
  {
    value: 'local',
    title: 'Standard platform login',
    badge: 'Default',
    description:
      'Admins receive activation links and sign in with email, password, and OTP. This keeps the current flow unchanged.',
  },
  {
    value: 'microsoft',
    title: 'Microsoft Entra ID SSO',
    badge: 'Optional',
    description:
      'Use Microsoft as the identity provider. Admin consent, assignment-required setup, directory sync, and RBAC mapping are configured after approval.',
  },
  {
    value: 'configure_later',
    title: 'Configure SSO later',
    badge: 'Flexible',
    description:
      'Start with standard platform login now and add Microsoft SSO later from organization settings.',
  },
  {
    value: 'google',
    title: 'Google Workspace SSO',
    badge: 'Coming soon',
    description:
      'Google Workspace support is planned, but not available in this implementation phase.',
    disabled: true,
  },
];

function getLoginMethodLabel(value) {
  return (
    LOGIN_METHOD_OPTIONS.find((option) => option.value === value)?.title ||
    'Standard platform login'
  );
}

export default function RegistrationPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successData, setSuccessData] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([
    'employee_pii',
    'identity_documents',
  ]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const [formData, setFormData] = useState({
    onboarding_token: '',
    requester_name: '',
    requester_email: '',
    requester_phone: '',
    organization_address: '',
    contact_email: '',
    contact_phone: '',
    admin1_full_name: '',
    admin1_email: '',
    admin2_full_name: '',
    admin2_email: '',
    business_reason: '',
    expected_data_categories_text: '',
    expected_volume: '',
    preferred_login_method: 'local',
    microsoft_tenant_hint: '',
  });

  const completion = useMemo(() => {
    const requiredFields = [
      'onboarding_token',
      'requester_name',
      'requester_email',
      'contact_email',
      'contact_phone',
      'admin1_full_name',
      'admin1_email',
      'admin2_full_name',
      'admin2_email',
    ];

    const completed = requiredFields.filter((field) => formData[field]?.trim()).length;
    return Math.round((completed / requiredFields.length) * 100);
  }, [formData]);

  const allCategories = useMemo(() => {
    const customCategories = formData.expected_data_categories_text
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

    return Array.from(new Set([...selectedCategories, ...customCategories]));
  }, [formData.expected_data_categories_text, selectedCategories]);

  const tokenLooksValid = formData.onboarding_token.trim().startsWith('pii_onb_live_');

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const toggleCategory = (category) => {
    setSelectedCategories((prev) => {
      if (prev.includes(category)) {
        return prev.filter((item) => item !== category);
      }

      return [...prev, category];
    });
  };

  const buildPayload = () => ({
    onboarding_token: formData.onboarding_token.trim(),
    requester_name: formData.requester_name.trim(),
    requester_email: formData.requester_email.trim(),
    requester_phone: formData.requester_phone.trim(),
    organization_address: formData.organization_address.trim(),
    contact_email: formData.contact_email.trim(),
    contact_phone: formData.contact_phone.trim(),
    admin1_full_name: formData.admin1_full_name.trim(),
    admin1_email: formData.admin1_email.trim(),
    admin2_full_name: formData.admin2_full_name.trim(),
    admin2_email: formData.admin2_email.trim(),
    business_reason: formData.business_reason.trim(),
    expected_data_categories: allCategories,
    expected_volume: formData.expected_volume.trim(),
    preferred_login_method: formData.preferred_login_method,
    microsoft_tenant_hint:
      formData.preferred_login_method === 'microsoft'
        ? formData.microsoft_tenant_hint.trim()
        : '',
  });

  const validateForm = () => {
    if (!formData.onboarding_token.trim()) {
      return 'Consent Platform onboarding token is required.';
    }

    if (!tokenLooksValid) {
      return 'The onboarding token should start with pii_onb_live_. Please copy the full token from the Consent Platform.';
    }

    if (!formData.requester_name.trim() || !formData.requester_email.trim()) {
      return 'Requester name and requester email are required.';
    }

    if (!formData.contact_email.trim() || !formData.contact_phone.trim()) {
      return 'Organization contact email and phone are required.';
    }

    if (!formData.admin1_full_name.trim() || !formData.admin1_email.trim()) {
      return 'Primary Organization Super Admin name and email are required.';
    }

    if (!formData.admin2_full_name.trim() || !formData.admin2_email.trim()) {
      return 'Privacy Operations Admin name and email are required.';
    }

    if (
      formData.admin1_email.trim().toLowerCase() ===
      formData.admin2_email.trim().toLowerCase()
    ) {
      return 'Super Admin and Privacy Operations Admin must use different email addresses.';
    }

    if (formData.preferred_login_method === 'google') {
      return 'Google Workspace SSO is coming soon. Choose standard login, Microsoft SSO, or configure later.';
    }

    return '';
  };

  const selectedLoginMethod = useMemo(
    () =>
      LOGIN_METHOD_OPTIONS.find(
        (option) => option.value === formData.preferred_login_method,
      ) || LOGIN_METHOD_OPTIONS[0],
    [formData.preferred_login_method],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const res = await orgAPI.registerOrganization(buildPayload());
      setSuccessData(res.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          'Onboarding request failed. Please check your token and try again.',
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  if (successData) {
    return (
      <div className="min-h-screen bg-slate-50 px-6 py-8 sm:px-8 lg:px-12 xl:px-16">
        <div className="mb-6">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-950"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to landing
          </button>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_420px]">
          <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                <CheckCircle className="h-9 w-9" />
              </div>

              <div>
                <h1 className="text-3xl font-bold text-slate-950">
                  Onboarding request submitted
                </h1>
                <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">
                  Your request has been submitted for PII Platform Admin review.
                  Tenant infrastructure, admin users, API keys, and default RBAC
                  groups will be created only after approval.
                </p>
              </div>
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-3">
              <SummaryTile label="Request Code" value={successData.request_code} mono />
              <SummaryTile label="Status" value={successData.status} />
              <SummaryTile
                label="Next Step"
                value="Platform Admin Review"
              />
            </div>

            <div className="mt-8 rounded-2xl border border-orange-100 bg-orange-50 p-5">
              <h3 className="font-semibold text-slate-950">
                What happens next?
              </h3>
              <div className="mt-4 grid gap-4 md:grid-cols-3">
                <NextStep title="Review" description="The PII Platform Admin reviews the request details." />
                <NextStep title="Approval" description="On approval, the PII tenant workspace is provisioned." />
                <NextStep
                  title={formData.preferred_login_method === 'microsoft' ? 'Identity setup' : 'Activation'}
                  description={
                    formData.preferred_login_method === 'microsoft'
                      ? 'Microsoft SSO setup will be completed after approval. Local activation remains available as a fallback.'
                      : 'The two admins receive activation emails.'
                  }
                />
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-950">
              Keep this request code
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Use this reference when contacting the platform team about your
              onboarding status.
            </p>

            <div className="mt-5 rounded-2xl bg-slate-950 p-4 text-white">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Request Code
              </p>
              <p className="mt-2 break-all font-mono text-lg font-bold">
                {successData.request_code}
              </p>
            </div>

            <button
              onClick={() => navigate('/')}
              className="mt-6 w-full rounded-xl bg-orange-600 px-5 py-3 text-sm font-semibold text-white hover:bg-orange-700"
            >
              Back to landing page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-8 sm:px-8 lg:px-12 xl:px-16">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-950"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to landing
        </button>

        <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700">
          <ShieldCheck className="h-3.5 w-3.5" />
          Approval-based provisioning
        </div>
      </div>

      <div className="grid w-full gap-6 xl:grid-cols-[1fr_420px]">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-gradient-to-r from-orange-50 via-white to-amber-50 p-8">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex items-start gap-4">
                <div className="rounded-2xl bg-orange-600 p-3 text-white shadow-sm">
                  <KeyRound className="h-6 w-6" />
                </div>

                <div>
                  <h1 className="text-3xl font-bold text-slate-950">
                    Request PII Platform Onboarding
                  </h1>
                  <p className="mt-2 max-w-5xl text-sm leading-6 text-slate-600">
                    Submit a Consent Platform generated onboarding token and
                    proposed PII admin details. The PII Platform validates the
                    token internally and creates only a pending request until a
                    Platform Admin approves it.
                  </p>
                </div>
              </div>

              <div className="rounded-2xl border border-orange-100 bg-white p-4 shadow-sm lg:w-64">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Completion
                  </p>
                  <p className="text-sm font-bold text-orange-700">{completion}%</p>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-orange-600 transition-all"
                    style={{ width: `${completion}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="p-8">
            {error && (
              <div className="mb-6 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              <Section
                icon={<KeyRound className="h-5 w-5" />}
                title="Consent Platform Onboarding Token"
                description="Paste the full onboarding token generated from the Consent Platform integration page. The PII Platform will validate it internally."
              >
                <Field label="Onboarding Token *" className="xl:col-span-3">
                  <div className="relative">
                    <textarea
                      name="onboarding_token"
                      value={formData.onboarding_token}
                      onChange={handleChange}
                      rows="3"
                      placeholder="pii_onb_live_..."
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 pr-12 font-mono text-sm outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                      required
                    />

                    <div className="absolute right-3 top-3">
                      {formData.onboarding_token ? (
                        tokenLooksValid ? (
                          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                            <Check className="h-4 w-4" />
                          </span>
                        ) : (
                          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-red-50 text-red-600">
                            <X className="h-4 w-4" />
                          </span>
                        )
                      ) : null}
                    </div>
                  </div>

                  <p className="mt-2 text-xs text-slate-500">
                    The token is not stored in readable form. It is used to verify
                    the Consent Platform organization during request submission.
                  </p>
                </Field>
              </Section>

              <Section
                icon={<UserRoundCog className="h-5 w-5" />}
                title="Requester Details"
                description="Who is submitting this PII onboarding request?"
              >
                <TextInput label="Requester Name *" name="requester_name" value={formData.requester_name} onChange={handleChange} required />
                <TextInput label="Requester Email *" name="requester_email" type="email" value={formData.requester_email} onChange={handleChange} required />
                <TextInput label="Requester Phone" name="requester_phone" value={formData.requester_phone} onChange={handleChange} />
              </Section>

              <Section
                icon={<Building2 className="h-5 w-5" />}
                title="Organization Contact"
                description="Operational contact information for the organization being onboarded."
              >
                <Field label="Organization Address" className="xl:col-span-3">
                  <textarea
                    name="organization_address"
                    value={formData.organization_address}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Registered or operating address"
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  />
                </Field>

                <TextInput label="Contact Email *" name="contact_email" type="email" value={formData.contact_email} onChange={handleChange} required />
                <TextInput label="Contact Phone *" name="contact_phone" value={formData.contact_phone} onChange={handleChange} required />
              </Section>

              <Section
                icon={<ShieldCheck className="h-5 w-5" />}
                title="PII Platform Admin Roles"
                description="These users will receive activation emails only after the PII Platform Admin approves this onboarding request."
              >
                <RoleCard
                  title="Primary Organization Super Admin"
                  groupName="Default group: Super Admin"
                  description="Full operational control for this organization's PII workspace, including admin users, groups, permissions, PII access governance, documents, audit views, ROPA, and retention workflows."
                >
                  <TextInput
                    label="Super Admin Full Name *"
                    name="admin1_full_name"
                    value={formData.admin1_full_name}
                    onChange={handleChange}
                    required
                  />

                  <TextInput
                    label="Super Admin Email *"
                    name="admin1_email"
                    type="email"
                    value={formData.admin1_email}
                    onChange={handleChange}
                    required
                  />
                </RoleCard>

                <RoleCard
                  title="Privacy Operations Admin"
                  groupName="Default group: Privacy Operations Admin"
                  description="Restricted operational admin for day-to-day privacy operations. This role can support identity, masked PII access, document review, audit search, and ROPA workflows without full access-control ownership."
                >
                  <TextInput
                    label="Privacy Operations Admin Full Name *"
                    name="admin2_full_name"
                    value={formData.admin2_full_name}
                    onChange={handleChange}
                    required
                  />

                  <TextInput
                    label="Privacy Operations Admin Email *"
                    name="admin2_email"
                    type="email"
                    value={formData.admin2_email}
                    onChange={handleChange}
                    required
                  />
                </RoleCard>
              </Section>

                            <Section
                icon={<KeyRound className="h-5 w-5" />}
                title="Identity & Login Setup"
                description="Choose how admins should access this organization. Standard platform login remains available; Microsoft SSO can be added without making onboarding dependent on Microsoft."
              >
                <div className="xl:col-span-3">
                  <div className="grid gap-4 md:grid-cols-2">
                    {LOGIN_METHOD_OPTIONS.map((option) => (
                      <IdentityOptionCard
                        key={option.value}
                        option={option}
                        selected={formData.preferred_login_method === option.value}
                        onSelect={() => {
                          if (option.disabled) return;

                          setFormData((prev) => ({
                            ...prev,
                            preferred_login_method: option.value,
                            microsoft_tenant_hint:
                              option.value === 'microsoft'
                                ? prev.microsoft_tenant_hint
                                : '',
                          }));
                          setError('');
                        }}
                      />
                    ))}
                  </div>

                  {formData.preferred_login_method === 'microsoft' && (
                    <div className="mt-5 rounded-2xl border border-orange-100 bg-orange-50 p-5">
                      <h4 className="font-semibold text-slate-950">
                        Microsoft setup after approval
                      </h4>

                      <p className="mt-2 text-sm leading-6 text-slate-600">
                        After approval, the organization admin will connect Microsoft Entra ID,
                        complete admin consent, enable assignment-required access in Microsoft,
                        and map selected users/groups to PII Platform RBAC groups.
                      </p>

                      <div className="mt-4">
                        <label className="mb-2 block text-sm font-medium text-slate-700">
                          Microsoft tenant/domain hint
                        </label>

                        <input
                          name="microsoft_tenant_hint"
                          value={formData.microsoft_tenant_hint}
                          onChange={handleChange}
                          placeholder="Example: contoso.onmicrosoft.com or tenant ID"
                          className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                        />

                        <p className="mt-2 text-xs text-slate-500">
                          Optional for now. The actual tenant ID will be verified during Microsoft consent.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </Section>

              <Section
                icon={<ClipboardCheck className="h-5 w-5" />}
                title="Business Context"
                description="Help the platform admin understand the scope and purpose of this onboarding request."
              >
                <Field label="Business Reason" className="xl:col-span-3">
                  <textarea
                    name="business_reason"
                    value={formData.business_reason}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Example: Enable secure PII ingestion and document governance for employee consent workflows."
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  />
                </Field>

                <Field label="Expected Data Categories" className="xl:col-span-2">
                  <div className="flex flex-wrap gap-2">
                    {CATEGORY_OPTIONS.map((category) => {
                      const selected = selectedCategories.includes(category);

                      return (
                        <button
                          key={category}
                          type="button"
                          onClick={() => toggleCategory(category)}
                          className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                            selected
                              ? 'bg-orange-600 text-white'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                        >
                          {category.replaceAll('_', ' ')}
                        </button>
                      );
                    })}
                  </div>

                  <input
                    name="expected_data_categories_text"
                    value={formData.expected_data_categories_text}
                    onChange={handleChange}
                    placeholder="Add custom categories, comma separated"
                    className="mt-3 w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  />
                </Field>

                <TextInput
                  label="Expected Volume"
                  name="expected_volume"
                  value={formData.expected_volume}
                  onChange={handleChange}
                  placeholder="Example: 1000-5000 records"
                />
              </Section>

              <div className="flex flex-col gap-3 rounded-2xl border border-orange-100 bg-orange-50 p-5 md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-3">
                  <Info className="mt-0.5 h-5 w-5 shrink-0 text-orange-700" />
                  <div>
                    <h4 className="font-semibold text-slate-950">
                      Submission creates a pending request only
                    </h4>
                    <p className="mt-1 text-sm leading-6 text-slate-600">
                      Tenant schema, admin accounts, API keys, and RBAC groups
                      are created only after platform admin approval.
                    </p>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {loading ? 'Submitting request...' : 'Submit Onboarding Request'}
                </button>
              </div>
            </form>
          </div>
        </div>

        <aside className="space-y-6 xl:sticky xl:top-8 xl:self-start">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-3">
              <div className="rounded-2xl bg-orange-100 p-3 text-orange-700">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-950">
                  Request Summary
                </h2>
                <p className="text-sm text-slate-500">
                  Review before submitting
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <SummaryLine
                label="Token format"
                value={
                  formData.onboarding_token
                    ? tokenLooksValid
                      ? 'Looks valid'
                      : 'Invalid prefix'
                    : 'Missing'
                }
                good={tokenLooksValid}
                warn={!formData.onboarding_token}
              />

              <SummaryLine
                label="Requester"
                value={formData.requester_name || 'Not provided'}
                good={!!formData.requester_name}
              />

              <SummaryLine
                label="Super Admin"
                value={formData.admin1_email || 'Not provided'}
                good={!!formData.admin1_email}
              />

              <SummaryLine
                label="Privacy Ops Admin"
                value={formData.admin2_email || 'Not provided'}
                good={!!formData.admin2_email}
              />

              <SummaryLine
                label="Login method"
                value={getLoginMethodLabel(formData.preferred_login_method)}
                good={formData.preferred_login_method === 'local'}
                warn={formData.preferred_login_method !== 'local'}
              />

              <SummaryLine
                label="Data categories"
                value={allCategories.length ? `${allCategories.length} selected` : 'Optional'}
                good={allCategories.length > 0}
                warn={allCategories.length === 0}
              />
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-slate-950 p-6 text-white shadow-sm">
            <h2 className="text-lg font-bold">Provisioning workflow</h2>

            <div className="mt-5 space-y-4">
              <FlowItem number="1" title="Token validation" description="PII validates the onboarding token internally with Consent Platform." />
              <FlowItem number="2" title="Pending request" description="A review request is created without provisioning infra." />
              <FlowItem number="3" title="Admin approval" description="PII Platform Admin approves or rejects the request." />
              <FlowItem number="4" title="Workspace creation" description="Tenant, admins, API key, and RBAC are created on approval." />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Section({ icon, title, description, children }) {
  return (
    <div className="border-b border-slate-200 pb-8 last:border-b-0">
      <div className="mb-5 flex items-start gap-3">
        {icon && (
          <div className="rounded-xl bg-orange-50 p-2 text-orange-700">
            {icon}
          </div>
        )}

        <div>
          <h3 className="text-lg font-semibold text-slate-950">{title}</h3>
          {description && (
            <p className="mt-1 max-w-5xl text-sm leading-6 text-slate-600">
              {description}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
        {children}
      </div>
    </div>
  );
}

function Field({ label, className = '', children }) {
  return (
    <div className={className}>
      <label className="mb-2 block text-sm font-medium text-slate-700">
        {label}
      </label>
      {children}
    </div>
  );
}

function TextInput({
  label,
  name,
  value,
  onChange,
  type = 'text',
  required = false,
  placeholder = '',
}) {
  return (
    <Field label={label}>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
        required={required}
      />
    </Field>
  );
}

function RoleCard({ title, groupName, description, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 md:col-span-2 xl:col-span-3">
      <div className="mb-5">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="text-base font-semibold text-slate-950">
            {title}
          </h4>

          <span className="rounded-full bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-700 ring-1 ring-orange-100">
            {groupName}
          </span>
        </div>

        <p className="mt-2 max-w-5xl text-sm leading-6 text-slate-600">
          {description}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {children}
      </div>
    </div>
  );
}

function IdentityOptionCard({ option, selected, onSelect }) {
  return (
    <button
      type="button"
      disabled={option.disabled}
      onClick={onSelect}
      className={`rounded-2xl border p-5 text-left transition ${
        selected
          ? 'border-orange-300 bg-orange-50 ring-2 ring-orange-100'
          : option.disabled
            ? 'cursor-not-allowed border-slate-200 bg-slate-50 opacity-70'
            : 'border-slate-200 bg-white hover:border-orange-200 hover:bg-orange-50/40'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="font-semibold text-slate-950">{option.title}</h4>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            {option.description}
          </p>
        </div>

        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${
            option.disabled
              ? 'bg-slate-100 text-slate-500 ring-slate-200'
              : selected
                ? 'bg-orange-600 text-white ring-orange-600'
                : 'bg-slate-100 text-slate-700 ring-slate-200'
          }`}
        >
          {option.badge}
        </span>
      </div>

      {selected && !option.disabled && (
        <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-orange-700">
          <Check className="h-4 w-4" />
          Selected
        </div>
      )}
    </button>
  );
}

function SummaryLine({ label, value, good = false, warn = false }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-3 last:border-b-0 last:pb-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span
        className={`text-right text-sm font-semibold ${
          good
            ? 'text-emerald-700'
            : warn
              ? 'text-orange-700'
              : 'text-slate-700'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function FlowItem({ number, title, description }) {
  return (
    <div className="flex gap-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-orange-500 text-sm font-bold text-white">
        {number}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        <p className="mt-1 text-xs leading-5 text-slate-300">{description}</p>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, mono = false }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p
        className={`mt-2 text-sm font-bold text-slate-950 ${
          mono ? 'break-all font-mono' : ''
        }`}
      >
        {value || '—'}
      </p>
    </div>
  );
}

function NextStep({ title, description }) {
  return (
    <div>
      <h4 className="font-semibold text-slate-950">{title}</h4>
      <p className="mt-1 text-sm leading-6 text-slate-600">{description}</p>
    </div>
  );
}